import { motion } from "framer-motion";
import { Logo } from "../components/Logo";

function LegalShell({ title, updated, children, testid }) {
  return (
    <div className="min-h-screen bg-parchment texture-grain" data-testid={testid}>
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="flex items-center justify-between mb-10">
          <a href="/" className="flex items-center gap-3 group" data-testid="legal-home-link">
            <Logo size={44} />
            <span className="font-serif text-lg text-river">Living Fountain Christian Centre</span>
          </a>
          <a href="/" className="text-xs font-semibold text-water hover:text-river transition-colors" data-testid="legal-back-link">← Back home</a>
        </div>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="font-serif text-4xl sm:text-5xl text-river mb-2">{title}</h1>
          <p className="text-xs font-mono tracking-widest uppercase text-muted-foreground mb-10">Last updated: {updated}</p>
          <div className="space-y-8 text-sm leading-relaxed text-river/85">{children}</div>
        </motion.div>
        <div className="mt-14 pt-6 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
          <span>Living Fountain Christian Centre · London, Ontario</span>
          <span className="flex gap-4">
            <a href="/privacy" className="hover:text-river transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-river transition-colors">Terms</a>
          </span>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-serif text-xl text-river mb-2">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms & Conditions" updated="October 2026" testid="terms-page">
      <Section title="1. Who we are">
        <p>Living Fountain Christian Centre ("LFCC", "we", "our") is a church in London, Ontario. These terms govern your use of our website, digital connection card, surveys, and SMS messaging program.</p>
      </Section>
      <Section title="2. SMS Messaging Program">
        <p>When you complete our connection card or Children's Ministry registration and explicitly opt in to text contact, you agree to receive SMS messages from LFCC. Messages include follow-up and care messages, event and service invitations, and Children's Ministry pickup notifications.</p>
        <p>Message frequency varies. Message and data rates may apply.</p>
        <p>You can opt out at any time by replying <strong>STOP</strong> to any message. After opting out you will receive a single confirmation and no further messages unless you opt in again. For help, reply <strong>HELP</strong> or contact us at info@thefountainonline.com.</p>
        <p>Carriers are not liable for delayed or undelivered messages. Supported on major Canadian and US carriers.</p>
      </Section>
      <Section title="3. Consent">
        <p>We only send text messages to people who have explicitly requested contact by selecting "Yes" when asked "Would you like someone from LFCC to contact you?" and choosing a text contact method on our forms. Consent is not a condition of attending, giving, or receiving any service.</p>
      </Section>
      <Section title="4. Appropriate use">
        <p>Our digital forms are intended for genuine connection with our church family. Please do not submit false information or another person's contact details without their knowledge.</p>
      </Section>
      <Section title="5. Privacy">
        <p>Your use of our services is also governed by our <a href="/privacy" className="text-water underline underline-offset-2">Privacy Policy</a>.</p>
      </Section>
      <Section title="6. Changes">
        <p>We may update these terms from time to time. The current version is always available at this page.</p>
      </Section>
      <Section title="7. Contact">
        <p>Questions about these terms: <strong>info@thefountainonline.com</strong> — Living Fountain Christian Centre, London, Ontario, Canada.</p>
      </Section>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy" updated="October 2026" testid="privacy-page">
      <Section title="1. What we collect">
        <p>Through our connection card, surveys, and Children's Ministry registration we collect the information you choose to give us: name, phone number, email, age group, household and family details, visit information, prayer requests, and — for children in our care — health or support notes needed to keep them safe.</p>
      </Section>
      <Section title="2. How we use it">
        <p>We use this information solely for church ministry: welcoming and following up with visitors, pastoral care, coordinating volunteers, keeping children safe during our programs, and understanding how well we serve our guests.</p>
      </Section>
      <Section title="3. What we never do">
        <p>We do not sell, rent, or share your personal information with third parties for their marketing purposes. <strong>SMS consent and phone numbers collected for text messaging are never shared with or sold to any third party.</strong></p>
      </Section>
      <Section title="4. SMS data">
        <p>If you opt in to text messages, your number is used only to send the messages described in our <a href="/terms" className="text-water underline underline-offset-2">Terms</a>. You may reply STOP at any time to opt out; your choice is honoured immediately.</p>
      </Section>
      <Section title="5. Children's information">
        <p>Children's Ministry records (health notes, pickup authorizations, incident reports) are restricted to authorized Children's Ministry leaders only and are never used for any other purpose.</p>
      </Section>
      <Section title="6. Access and removal">
        <p>You may ask us at any time to see, correct, or delete the information we hold about you or your child by emailing info@thefountainonline.com.</p>
      </Section>
      <Section title="7. Data security">
        <p>Information is stored in access-controlled systems limited to authorized LFCC leaders. Sensitive pastoral and child-related notes carry additional access restrictions.</p>
      </Section>
      <Section title="8. Contact">
        <p>Privacy questions: <strong>info@thefountainonline.com</strong> — Living Fountain Christian Centre, London, Ontario, Canada.</p>
      </Section>
    </LegalShell>
  );
}

export default TermsPage;
